/**
 * @file Orval 请求实例适配器
 * @description 统一复用项目 axios 拦截器，并对 GET 请求做同参数并发去重。
 */
import type { AxiosRequestConfig, AxiosResponse } from 'axios'
import type { CustomAxiosRequestConfig } from './request'
import instance, { baseURL } from './request'
import {
  backgroundRequestConfig,
  interactiveRequestConfig
} from '@/utils/requestPriority'

/** GET 参数稳定序列化时支持的值类型。 */
type SerializableParam =
  | string
  | number
  | boolean
  | Date
  | null
  | undefined
  | URLSearchParams
  | SerializableParam[]
  | { [key: string]: SerializableParam }

/** in-flight 缓存只复用 Promise，不直接读取响应体。 */
type InFlightGetResponse = Promise<AxiosResponse<never>>

/** GET 请求 in-flight 缓存 */
const inFlightGetRequests = new Map<string, InFlightGetResponse>()

/** 稳定序列化 GET 参数 */
const stableSerialize = (value: SerializableParam): string => {
  if (value === null || value === undefined) {
    return ''
  }
  if (value instanceof URLSearchParams) {
    return JSON.stringify(
      Array.from(value.entries()).sort(([a], [b]) => a.localeCompare(b))
    )
  }
  if (Array.isArray(value)) {
    return JSON.stringify(value.map((item) => stableSerialize(item)))
  }
  if (value instanceof Date) {
    return value.toISOString()
  }
  if (typeof value === 'object') {
    const record = value as Record<string, SerializableParam>
    return JSON.stringify(
      Object.keys(record)
        .sort()
        .map((key) => [key, stableSerialize(record[key])])
    )
  }
  return JSON.stringify(value)
}

/** 构建 GET 去重键 */
const buildGetDedupeKey = (config: AxiosRequestConfig): string => {
  const method = (config.method || 'GET').toUpperCase()
  const requestBaseUrl = config.baseURL || baseURL || ''
  const url = config.url || ''
  const params = stableSerialize(config.params as SerializableParam)
  return `${method}:${requestBaseUrl}:${url}:${params}`
}

/** 执行请求，GET 同参数并发时复用同一个 Promise */
const requestWithGetDedupe = <T>(
  config: AxiosRequestConfig
): Promise<AxiosResponse<T>> => {
  const method = (config.method || 'GET').toUpperCase()
  if (method !== 'GET') {
    return instance<T>(config)
  }
  const key = buildGetDedupeKey(config)
  const existing = inFlightGetRequests.get(key)
  if (existing) {
    return existing as Promise<AxiosResponse<T>>
  }
  const pending = instance<T>(config).finally(() => {
    inFlightGetRequests.delete(key)
  })
  inFlightGetRequests.set(key, pending as InFlightGetResponse)
  return pending
}

/**
 * Orval 自定义 mutator
 * 复用已有的 axios 实例（含请求/响应拦截器）
 */
export const customInstance = <T>(
  config: AxiosRequestConfig
): Promise<AxiosResponse<T>> => {
  return requestWithGetDedupe<T>(config)
}

/** 交互优先级请求（首屏可见、用户操作） */
export const interactiveInstance = <T>(
  config: AxiosRequestConfig
): Promise<AxiosResponse<T>> => {
  return requestWithGetDedupe<T>({
    ...config,
    ...interactiveRequestConfig
  } as CustomAxiosRequestConfig)
}

/** 背景优先级请求（可延迟、非首屏） */
export const backgroundInstance = <T>(
  config: AxiosRequestConfig
): Promise<AxiosResponse<T>> => {
  return requestWithGetDedupe<T>({
    ...config,
    ...backgroundRequestConfig
  } as CustomAxiosRequestConfig)
}

export default customInstance
